import React, { Component } from 'react'
import {
  Bar,
  BarChart,
  Brush,
  CartesianGrid,
  Cell,
  Legend,
  Line,
  LineChart,
  Pie,
  PieChart,
  ResponsiveContainer,
  Sector,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts'

import Rainbow from 'rainbowvis.js'
import moment from 'moment'

import _, { sum } from 'lodash'
import { formatNumber, getColorRamp } from '../../utils/utils'

function determineGranularity(startDt, endDt) {
  let numDays = Math.abs(moment(startDt).diff(endDt, 'days'))

  let granularity
  if (numDays < 1) {
    granularity = 'hour'
  } else if (numDays < 35) {
    granularity = 'day'
  } else if (numDays < 370) {
    granularity = 'month'
  } else {
    granularity = 'year'
  }

  let numDataPoints = Math.abs(moment(startDt).diff(endDt, granularity))

  return {
    granularity: granularity,
    numberOfPoints: numDataPoints,
  }
}

export function granularityDescriptor(startDt, endDt, plural = false) {
  let g = determineGranularity(startDt, endDt)

  switch (g.granularity) {
    case 'year':
      return plural ? 'anuais' : 'anual'
    case 'month':
      return plural ? 'mensais' : 'mensal'
    case 'day':
      return plural ? 'diárias' : 'diária'
    case 'hour':
      return plural ? 'horárias' : 'horária'
    default:
      return ''
  }
}

function granularityFormatting(granularity, withParent = false) {
  switch (granularity) {
    case 'year':
      return '[\']YY'
    case 'month':
      return withParent ? 'MMM[ \']YY' : 'MMM'
    case 'day':
      return withParent ? 'D[-]MMM' : 'D'
    case 'hour':
      return 'HH[:00]'
  }
}

function formatTimeLabel(dt, index, granularity, totalLabels) {
  if (totalLabels < 20) {
    return moment(dt).format(granularityFormatting(granularity, false))
  }
  return moment(dt).format(granularityFormatting(granularity, false))
}

export class PieChartByAttribute extends Component {
  render() {
    const groupAttr = this.props.groupAttribute
    const sumAttr = this.props.sumAttribute

    let data = this.props.data.reduce((res, value) => {
      res[value[groupAttr]] ? (res[value[groupAttr]] += value[sumAttr]) : (res[value[groupAttr]] = value[sumAttr])
      return res
    }, {})

    let total = Object.keys(data).reduce((sum, key) => sum + data[key] || 0, 0)

    let _data = []
    for (const key of Object.keys(data)) {
      _data.push([key, data[key], data[key] / total])
    }

    _data = _data.sort((a, b) => b[2] - a[2])

    // Aggregate if we have too many values to display

    if (_data.length > 10) {
      let breakpoint = _data[5][2]

      let _agg_data = []
      let otherSum = [0, 0]
      for (const kv of _data) {
        if (kv[2] >= breakpoint) {
          _agg_data.push(kv)
        } else {
          otherSum[0] += kv[1]
          otherSum[1] += kv[2]
        }
      }
      _agg_data.push(['Outros', otherSum[0], otherSum[1]])
      _data = _agg_data
    }

    // Displace small values so that the labels dont overlap

    data = {}
    for (let index = 0; index < _data.length; index++) {
      let big = index
      let small = _data.length - 1 - index

      if (big === small) {
        data[_data[big][0]] = _data[big][1]
        break
      }

      data[_data[big][0]] = _data[big][1]
      data[_data[small][0]] = _data[small][1]
    }

    const colorRamp = getColorRamp('#000028', '#009999', Object.keys(data).length)

    data = Object.entries(data).map((d, ix) => {
      return { name: d[0], value: d[1], fill: colorRamp[ix] }
    })

    const renderCustomizedLabel = ({
      cx,
      cy,
      midAngle,
      innerRadius,
      outerRadius,
      startAngle,
      endAngle,
      fill,
      payload,
      percent,
      x,
      y,
    }) => {
      const RADIAN = Math.PI / 180

      const sin = Math.sin(-RADIAN * midAngle)
      const cos = Math.cos(-RADIAN * midAngle)
      const sx = cx + (outerRadius + 2) * cos
      const sy = cy + (outerRadius + 2) * sin
      const mx = cx + (outerRadius + 15) * cos
      const my = cy + (outerRadius + 15) * sin
      const textAnchor = cos >= 0 ? 'start' : 'end'

      return (
        <g>
          <line x1={sx} y1={sy} x2={mx} y2={my} strokeWidth="1" stroke={fill} />
          <circle cx={mx} cy={my} r={2} fill={fill} stroke="none" />
          <text x={mx + (cos >= 0 ? 1 : -1) * 12} y={my} textAnchor={textAnchor} fill="#333" fontSize="14px">
            {payload.name}
          </text>
          <text x={mx + (cos >= 0 ? 1 : -1) * 12} y={my} dy={18} textAnchor={textAnchor} fill="#999" fontSize="12px">
            {formatNumber(payload.value, 0)} {`(${(percent * 100).toFixed(2)}%)`}
          </text>
        </g>
      )
    }

    return (
      <ResponsiveContainer width="100%" aspect={this.props.aspectRatio}>
        <PieChart>
          <Tooltip formatter={(value) => formatNumber(value)} />
          <Pie
            data={data}
            dataKey="value"
            // nameKey='name'
            innerRadius={40}
            outerRadius={60}
            // paddingAngle={5}
            labelLine={false}
            label={renderCustomizedLabel}
            fill="#82ca9d"
          />
        </PieChart>
      </ResponsiveContainer>
    )
  }
}

export class BarChartByAttributeRatio extends Component {
  render() {
    const groupAttr = this.props.groupAttribute
    const numAttr = this.props.numeratorAttribute
    const denAttr = this.props.denominatorAttribute

    let _data = {}
    this.props.data.forEach((d) => {
      if (!(d[groupAttr] in _data)) {
        _data[d[groupAttr]] = {
          sumNum: 0,
          sumDen: 0,
        }
      }
      _data[d[groupAttr]]['sumNum'] += d[numAttr]
      _data[d[groupAttr]]['sumDen'] += d[denAttr]
    })

    let data = []
    Object.keys(_data).map((key) => {
      data.push({
        name: key,
        value: parseFloat((_data[key]['sumNum'] / _data[key]['sumDen']).toFixed(3)),
      })
    })

    const colorRamp = getColorRamp('#000028', '#009999', data.length)

    const renderCustomizedLabel = (props) => {
      let x = props.x + props.viewBox.width / 2
      return (
        <g>
          <text x={x} y={props.y - 5} fill={props.stroke} fontSize={12} textAnchor="middle">
            {props.value} {this.props.label}
          </text>
        </g>
      )
    }

    return (
      <ResponsiveContainer width="100%" aspect={this.props.aspectRatio}>
        <BarChart
          width="100%"
          height="100%"
          data={data}
          margin={{
            top: 30,
            right: 30,
            left: 20,
            bottom: 5,
          }}
        >
          <XAxis dataKey="name" />
          <YAxis label={{ fontSize: '.5rem' }} />
          <Tooltip formatter={(value) => formatNumber(value, 3)} />
          <Bar dataKey="value" fill="#000028" label={renderCustomizedLabel} barSize={80}>
            {data.map((entry, index) => (
              <Cell key={`cell-${index}`} fill={colorRamp[index]} />
            ))}
          </Bar>
        </BarChart>
      </ResponsiveContainer>
    )
  }
}

export class BarChartByDoubleAttributeRatio extends Component {
  render() {
    const groupAttr = this.props.groupAttribute
    const numAttr1 = this.props.numeratorAttribute1
    const numAttr2 = this.props.numeratorAttribute2
    const denAttr = this.props.denominatorAttribute

    let _data = {}
    this.props.data.forEach((d) => {
      if (!(d[groupAttr] in _data)) {
        _data[d[groupAttr]] = {
          sumNum1: 0,
          sumNum2: 0,
          sumDen: 0,
        }
      }
      _data[d[groupAttr]]['sumNum1'] += d[numAttr1]
      _data[d[groupAttr]]['sumNum2'] += d[numAttr2]
      _data[d[groupAttr]]['sumDen'] += d[denAttr]
    })

    let data = []
    Object.keys(_data).map((key) => {
      data.push({
        name: key,
        [this.props.numerator1DisplayName]: parseFloat((_data[key]['sumNum1'] / _data[key]['sumDen']).toFixed(3)),
        [this.props.numerator2DisplayName]: parseFloat((_data[key]['sumNum2'] / _data[key]['sumDen']).toFixed(3)),
      })
    })

    // const colorRamp = getColorRamp('#000028', '#009999', data.length)

    const renderCustomizedLabel = (props) => {
      let x = props.x + props.viewBox.width / 2
      return (
        <g>
          <text x={x} y={props.y - 5} fill={props.stroke} fontSize={12} textAnchor="middle">
            {props.value} {this.props.label}
          </text>
        </g>
      )
    }

    return (
      <ResponsiveContainer width="100%" aspect={this.props.aspectRatio}>
        <BarChart
          width="100%"
          height="100%"
          data={data}
          margin={{
            top: 30,
            right: 30,
            left: 20,
            bottom: 5,
          }}
        >
          <XAxis dataKey="name" />
          <YAxis label={{ fontSize: '.5rem' }} />
          <Tooltip />
          <Bar dataKey={this.props.numerator1DisplayName} fill="#000028" label={renderCustomizedLabel} barSize={40} />
          <Bar dataKey={this.props.numerator2DisplayName} fill="#009999" label={renderCustomizedLabel} barSize={40} />
        </BarChart>
      </ResponsiveContainer>
    )
  }
}

export class TimelineByAttribute extends Component {
  render() {
    const groupbyAttribute = this.props.groupbyAttribute
    const sumAttribute = this.props.sumAttribute

    const timeGranularity = determineGranularity(
      this.props.data[0].opDt,
      this.props.data[this.props.data.length - 1].opDt
    )

    let data = _.chain(this.props.data)
      .groupBy((v) => v.opDt.startOf(timeGranularity.granularity))
      .map((arr, key) => {
        let fts = _.groupBy(arr, (v) => v[groupbyAttribute])
        Object.keys(fts).forEach((k) => {
          fts[k] = _.sumBy(fts[k], sumAttribute)
        })
        return {
          time: formatTimeLabel(key, key, timeGranularity.granularity, timeGranularity.numberOfPoints),
          ...fts,
        }
      })
      .value()

    let features = []
    for (const d of data) {
      let fets = Object.keys(d).filter((f) => f !== 'time')
      for (const f of fets) {
        if (!features.includes(f)) features.push(f)
      }
    }

    if (this.props.total) {
      for (const dp of data) {
        dp['Total'] = features.map((f) => dp[f]).reduce((a, b) => a + b)
      }
    }

    // console.log(data)

    const strokes = getColorRamp('#000028', '#009999', features.length)

    return (
      <ResponsiveContainer width="100%" aspect={this.props.aspectRatio}>
        <LineChart
          data={data}
          margin={{
            top: 5,
            right: 30,
            left: 20,
            bottom: 5,
          }}
        >
          <CartesianGrid strokeDasharray="5 5" />
          <XAxis dataKey="time" />
          <YAxis />
          <Tooltip formatter={(value) => formatNumber(value)} />
          <Legend />
          {/* <Brush dataKey='time'/> */}
          {features.map((f, ix) => (
            <Line key={f} type="monotone" dataKey={f} stroke={strokes[ix]} strokeWidth="2px" />
          ))}
          {this.props.total && <Line key="total" type="monotone" dataKey="Total" stroke="#ff9000" strokeWidth="2px" />}
        </LineChart>
      </ResponsiveContainer>
    )
  }
}

export class Timeline extends Component {
  render() {
    const sumAttribute = this.props.sumAttribute

    let data = {}

    this.props.data.forEach((value) => {
      const day = moment(value.opDt).date()

      if (!(day in data)) {
        data[day] = 0
      }

      data[day] += value[sumAttribute]
    })

    data = Object.entries(data).map((d, ix) => {
      return { name: d[0], value: d[1] }
    })

    return (
      <ResponsiveContainer width="100%" aspect={this.props.aspectRatio}>
        <LineChart
          data={data}
          margin={{
            top: 5,
            right: 30,
            left: 20,
            bottom: 5,
          }}
        >
          <CartesianGrid strokeDasharray="2 2" />
          <XAxis dataKey="name" />
          <YAxis />
          <Tooltip formatter={(value) => formatNumber(value)} />
          <Line type="monotone" dataKey="value" stroke="#000028" strokeWidth="2px" />
        </LineChart>
      </ResponsiveContainer>
    )
  }
}

export class TimelineByTwoAttributes extends Component {
  render() {
    // const groupbyAttribute = this.props.groupbyAttribute
    const sumAttribute1 = this.props.sumAttribute1
    const sumAttribute2 = this.props.sumAttribute2

    const timeGranularity = determineGranularity(
      this.props.data[0].opDt,
      this.props.data[this.props.data.length - 1].opDt
    )

    let data = _.chain(this.props.data)
      .groupBy((v) => v.opDt.startOf(timeGranularity.granularity))
      .map((arr, key) => {
        return {
          time: formatTimeLabel(key, key, timeGranularity.granularity, timeGranularity.numberOfPoints),
          [sumAttribute1]: _.sumBy(arr, sumAttribute1),
          [sumAttribute2]: _.sumBy(arr, sumAttribute2),
        }
      })
      .value()

    if (this.props.total) {
      for (const dp of data) {
        dp['Total'] = dp[sumAttribute1] + dp[sumAttribute2]
      }
    }

    const strokes = getColorRamp('#000028', '#009999', 2)
    return (
      <ResponsiveContainer width="100%" aspect={this.props.aspectRatio}>
        <LineChart
          data={data}
          margin={{
            top: 5,
            right: 30,
            left: 20,
            bottom: 5,
          }}
        >
          <CartesianGrid strokeDasharray="5 5" />
          <XAxis dataKey="time" />
          <YAxis />
          <Tooltip formatter={(value) => formatNumber(value)} />
          <Legend />
          <Line key={1} type="monotone" dataKey="consumptionTotal" stroke={strokes[0]} strokeWidth="2px" />
          <Line key={2} type="monotone" dataKey={sumAttribute2} stroke={strokes[1]} strokeWidth="2px" />
          {this.props.total && <Line key="total" type="monotone" dataKey="Total" stroke="#ff9000" strokeWidth="2px" />}
        </LineChart>
      </ResponsiveContainer>
    )
  }
}

export class TimelineOfDifferentAttributes extends Component {
  render() {
    // const groupbyAttribute = this.props.groupbyAttribute

    const attributes = this.props.attributes
    const filters = this.props.filters

    const timeGranularity = determineGranularity(
      this.props.data[0].opDt,
      this.props.data[this.props.data.length - 1].opDt
    )

    let data = _.chain(this.props.data)

    if (filters) {
      for (const filter of filters) {
        data = data.filter((v) => v[filter.attribute] == filter.value)
      }
    }

    data = data
      .groupBy((v) => v.opDt.startOf(timeGranularity.granularity))
      .map((arr, key) => {
        let fts = {}

        for (const attr of attributes) {
          fts[attr.label] = _.sumBy(arr, attr.name)
        }

        return {
          time: formatTimeLabel(key, key, timeGranularity.granularity, timeGranularity.numberOfPoints),
          ...fts,
        }
      })
      .value()

    let features = []
    for (const d of data) {
      let fets = Object.keys(d).filter((f) => f !== 'time')
      for (const f of fets) {
        if (!features.includes(f)) features.push(f)
      }
    }

    if (this.props.total) {
      for (const dp of data) {
        dp['Total'] = features.map((f) => dp[f]).reduce((a, b) => a + b)
      }
    }

    const strokes = getColorRamp('#000028', '#009999', features.length)

    return (
      <ResponsiveContainer width="100%" aspect={this.props.aspectRatio}>
        <LineChart
          data={data}
          margin={{
            top: 5,
            right: 30,
            left: 20,
            bottom: 5,
          }}
        >
          <CartesianGrid strokeDasharray="5 5" />
          <XAxis dataKey="time" />
          <YAxis />
          <Tooltip formatter={(value) => formatNumber(value)} />
          <Legend />

          {features.map((f, ix) => (
            <Line key={f} type="monotone" dataKey={f} stroke={strokes[ix]} strokeWidth="2px" />
          ))}

          {this.props.total && <Line key="total" type="monotone" dataKey="Total" stroke="#ff9000" strokeWidth="2px" />}
        </LineChart>
      </ResponsiveContainer>
    )
  }
}
